import React, { useMemo } from 'react';
import { Box, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10,
    marginBottom: 12,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  card: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '12px 12px 10px',
    minHeight: 82,
    boxSizing: 'border-box',
  },
  cardLabel: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  cardValue: {
    color: '#e5e7ff',
    fontSize: 16,
    fontWeight: 900,
    lineHeight: 1.25,
    wordBreak: 'break-word',
  },
  cardMeta: {
    color: '#8d93c9',
    fontSize: 11,
    lineHeight: 1.35,
    marginTop: 6,
  },
  summaryCard: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.2)',
    background: 'linear-gradient(180deg, rgba(17, 24, 50, 0.92), rgba(10, 14, 30, 0.9))',
    padding: '12px 12px 10px',
    marginBottom: 12,
  },
  summaryTitle: {
    color: '#e5e7ff',
    fontSize: 14,
    fontWeight: 900,
    marginBottom: 4,
  },
  summaryText: {
    color: '#c9cff8',
    fontSize: 12,
    lineHeight: 1.45,
  },
  sectionTitle: {
    color: '#9ea4d8',
    fontSize: 11,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  chip: {
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.28)',
    background: 'rgba(77, 121, 255, 0.12)',
    color: '#cfd6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
  },
  warningRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  warningCard: {
    borderRadius: 12,
    border: '1px solid rgba(255, 189, 92, 0.22)',
    background: 'rgba(38, 24, 10, 0.72)',
    padding: '10px 12px',
  },
  warningTitle: {
    color: '#ffd39a',
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.4,
  },
  warningText: {
    color: '#f6d9b1',
    fontSize: 11,
    lineHeight: 1.4,
    marginTop: 4,
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 12,
  },
}));

function toNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n < 1 ? 6 : 2,
  }).format(n);
}

function formatCompact(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(n);
}

function formatRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${n.toFixed(n >= 10 ? 1 : 2)}x`;
}

function formatQuality(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${n.toFixed(2)} / 5`;
}

function shortenPool(value) {
  const raw = String(value || '').trim();
  if (!/^0x[0-9a-fA-F]{40}$/.test(raw)) return raw || 'No pool source';
  return `${raw.slice(0, 6)}...${raw.slice(-4)}`;
}

function relativeFreshness(value) {
  const raw = String(value || '').trim();
  if (!raw) return 'No freshness data';
  const ts = new Date(raw).getTime();
  if (!Number.isFinite(ts)) return raw;
  const hours = Math.max(0, (Date.now() - ts) / (1000 * 60 * 60));
  if (hours < 1) return 'Updated <1h ago';
  if (hours < 24) return `Updated ${Math.round(hours)}h ago`;
  return `Updated ${Math.round(hours / 24)}d ago`;
}

function buildQuickChips(liquidityLatest, poolStats, tradeability) {
  const out = [];
  if (tradeability && tradeability.label) out.push(tradeability.label);
  if (liquidityLatest && liquidityLatest.price_source_dex) out.push(String(liquidityLatest.price_source_dex));
  if (poolStats && poolStats.quote_token) out.push(`Quote ${shortenPool(poolStats.quote_token)}`);
  if (poolStats && toNum(poolStats.quality) > 0) out.push(`Quality ${formatQuality(poolStats.quality)}`);
  return out.slice(0, 4);
}

function deriveTradeability(metadata, liquidityLatest, poolStats) {
  const liquidityUsd = toNum(liquidityLatest && liquidityLatest.liquidity_usd);
  const marketCapUsd = toNum(metadata && metadata.market_cap_usd);
  const ratio = liquidityUsd > 0 ? marketCapUsd / liquidityUsd : 0;
  const quality = toNum(poolStats && poolStats.quality);
  const tvl = toNum(poolStats && poolStats.tvl_usd);
  if (liquidityUsd <= 0) return {
    label: 'Liquidity confidence limited',
    text: 'No current liquidity snapshot is available, so tradeability should be treated cautiously.',
  };
  if ((liquidityUsd < 5000 || tvl < 5000) && quality > 0 && quality < 3) return {
    label: 'Dust-pool caution',
    text: 'Liquidity appears thin and pool quality is weak, so price discovery may be fragile and easily distorted.',
  };
  if (ratio >= 20 || (quality > 0 && quality < 2.5)) return {
    label: 'Thin structure',
    text: 'Valuation is running hot relative to available liquidity or pool quality, so slippage and reversals may be sharp.',
  };
  if (ratio >= 8 || liquidityUsd < 100000 || (quality > 0 && quality < 3.5)) return {
    label: 'Moderate tradeability',
    text: 'The token has usable market structure, but liquidity depth and pool quality still warrant caution for larger moves.',
  };
  return {
    label: 'Healthier tradeability',
    text: 'Liquidity support and available pool context look reasonably aligned for a token-level investigation workflow.',
  };
}

function deriveWarnings(metadata, liquidityLatest, poolStats) {
  const warnings = [];
  const liquidityUsd = toNum(liquidityLatest && liquidityLatest.liquidity_usd);
  const marketCapUsd = toNum(metadata && metadata.market_cap_usd);
  const supply = toNum(metadata && metadata.supply);
  const ratio = liquidityUsd > 0 ? marketCapUsd / liquidityUsd : 0;
  const quality = toNum(poolStats && poolStats.quality);
  const tvl = toNum(poolStats && poolStats.tvl_usd);
  const volume24h = toNum(poolStats && poolStats.volume_24h_usd);
  const lastSwapTs = String((poolStats && poolStats.last_swap_ts) || '').trim();
  const liquidityUpdatedAt = String((liquidityLatest && liquidityLatest.updated_at) || '').trim();

  if (liquidityUsd > 0 && liquidityUsd < 100000) {
    warnings.push({
      title: 'Low liquidity snapshot',
      text: `Latest reported liquidity is ${formatUsd(liquidityUsd)}, which may amplify slippage and volatility.`,
    });
  }

  if (ratio >= 20) {
    warnings.push({
      title: 'Weak market-cap / liquidity support',
      text: `Market cap is running at ${formatRatio(ratio)} of reported liquidity, so depth may be thin relative to valuation.`,
    });
  }

  if (quality > 0 && quality < 3) {
    warnings.push({
      title: 'Pool quality weak',
      text: `Current pool quality reads ${formatQuality(quality)}, which suggests lower confidence in market structure quality.`,
    });
  }

  if (tvl > 0 && tvl < 20000) {
    warnings.push({
      title: 'Dust-pool caution',
      text: `Pool TVL is only ${formatUsd(tvl)}, so reported liquidity may be fragile and easier to distort.`,
    });
  }

  if (volume24h > 0 && tvl > 0 && volume24h / tvl < 0.1) {
    warnings.push({
      title: 'Pool activity is light',
      text: `24h pool volume is ${formatUsd(volume24h)} against ${formatUsd(tvl)} TVL, suggesting shallow recent turnover.`,
    });
  }

  if (liquidityUpdatedAt && ((Date.now() - new Date(liquidityUpdatedAt).getTime()) / (1000 * 60 * 60)) > 24) {
    warnings.push({
      title: 'Stale liquidity source',
      text: `Latest liquidity source was ${relativeFreshness(liquidityUpdatedAt)}, so source freshness is limited.`,
    });
  }

  if (lastSwapTs && ((Date.now() - new Date(lastSwapTs).getTime()) / (1000 * 60 * 60)) > 24) {
    warnings.push({
      title: 'Pool activity may be stale',
      text: `Last observed pool swap was ${relativeFreshness(lastSwapTs)}, which may reduce confidence in live pool context.`,
    });
  }

  if (supply <= 0) {
    warnings.push({
      title: 'Supply confidence is limited',
      text: 'Supply is missing or zero in the current metadata snapshot, so supply-derived fields should be treated cautiously.',
    });
  }

  if (!warnings.length) {
    warnings.push({
      title: 'No immediate structure warnings',
      text: 'The current snapshot does not trigger the simple liquidity and supply heuristics used by this panel.',
    });
  }

  return warnings;
}

export default function MarketStructurePanel({ metadata, liquidity }) {
  const classes = useStyles();
  const liquidityLatest = liquidity && liquidity.latest ? liquidity.latest : null;
  const poolStats = liquidity && liquidity.pool_stats ? liquidity.pool_stats : null;
  const liquidityUsd = toNum(liquidityLatest && liquidityLatest.liquidity_usd);
  const marketCapUsd = toNum(metadata && metadata.market_cap_usd);
  const supply = toNum(metadata && metadata.supply);
  const categories = Array.isArray(metadata && metadata.categories) ? metadata.categories.slice(0, 4) : [];
  const ratio = liquidityUsd > 0 ? marketCapUsd / liquidityUsd : 0;
  const warnings = useMemo(() => deriveWarnings(metadata, liquidityLatest, poolStats), [metadata, liquidityLatest, poolStats]);
  const tradeability = useMemo(() => deriveTradeability(metadata, liquidityLatest, poolStats), [metadata, liquidityLatest, poolStats]);
  const sourceFreshness = relativeFreshness(liquidityLatest && liquidityLatest.updated_at);
  const quickChips = useMemo(() => buildQuickChips(liquidityLatest, poolStats, tradeability), [liquidityLatest, poolStats, tradeability]);

  const cards = [
    {
      label: 'Liquidity Source',
      value: liquidityLatest && liquidityLatest.price_source_dex ? liquidityLatest.price_source_dex : 'No DEX source',
      meta: liquidityLatest && liquidityLatest.price_source_type ? `Type ${liquidityLatest.price_source_type} • ${sourceFreshness}` : 'Latest liquidity source snapshot',
    },
    {
      label: 'Pool / Source',
      value: shortenPool(liquidityLatest && liquidityLatest.price_source_pool),
      meta: liquidityLatest && liquidityLatest.updated_at ? `Updated ${liquidityLatest.updated_at}` : 'No pool source reported',
    },
    {
      label: 'Liquidity Depth',
      value: formatUsd(liquidityUsd),
      meta: liquidityUsd > 0 ? 'Current reported liquidity snapshot' : 'Liquidity not available',
    },
    {
      label: 'MC / Liquidity',
      value: formatRatio(ratio),
      meta: ratio > 0 ? `${formatUsd(marketCapUsd)} market cap vs ${formatUsd(liquidityUsd)} liquidity` : 'Ratio unavailable',
    },
    {
      label: 'Supply',
      value: formatCompact(supply),
      meta: supply > 0 ? 'Supply from token metadata snapshot' : 'Supply is missing or low-confidence',
    },
    {
      label: 'Market Cap',
      value: formatUsd(marketCapUsd),
      meta: marketCapUsd > 0 ? 'Latest market-cap snapshot' : 'No market-cap snapshot',
    },
    {
      label: 'Pool TVL',
      value: formatUsd(poolStats && poolStats.tvl_usd),
      meta: poolStats && poolStats.quote_token ? `Quote token ${shortenPool(poolStats.quote_token)}` : 'No pool TVL snapshot',
    },
    {
      label: 'Pool Quality',
      value: formatQuality(poolStats && poolStats.quality),
      meta: poolStats && poolStats.last_swap_ts ? `Last swap ${relativeFreshness(poolStats.last_swap_ts)}` : 'No pool quality snapshot',
    },
    {
      label: '24h Pool Volume',
      value: formatUsd(poolStats && poolStats.volume_24h_usd),
      meta: poolStats && poolStats.volume_24h_usd ? 'Recent pool turnover from current pool stats' : 'No recent pool turnover snapshot',
    },
    {
      label: 'Source Freshness',
      value: sourceFreshness,
      meta: liquidityLatest && liquidityLatest.updated_at ? `Liquidity source refreshed ${liquidityLatest.updated_at}` : 'No liquidity freshness timestamp',
    },
  ];

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>Market Structure</Typography>
      <Divider className={classes.divider} />

      <Box className={classes.summaryCard}>
        <Typography className={classes.summaryTitle}>{tradeability.label}</Typography>
        <Typography className={classes.summaryText}>{tradeability.text}</Typography>
      </Box>

      {quickChips.length ? (
        <Box className={classes.chipsRow}>
          {quickChips.map((item) => (
            <Box key={item} className={classes.chip}>{item}</Box>
          ))}
        </Box>
      ) : null}

      <Box className={classes.grid}>
        {cards.map((card) => (
          <Box key={card.label} className={classes.card}>
            <Typography className={classes.cardLabel}>{card.label}</Typography>
            <Typography className={classes.cardValue}>{card.value}</Typography>
            <Typography className={classes.cardMeta}>{card.meta}</Typography>
          </Box>
        ))}
      </Box>

      <Typography className={classes.sectionTitle}>Classification</Typography>
      <Box className={classes.chipsRow}>
        {categories.length ? categories.map((category) => (
          <Box key={category} className={classes.chip}>{category}</Box>
        )) : <Typography className={classes.cardMeta}>No category tags available in the current metadata snapshot.</Typography>}
      </Box>

      <Typography className={classes.sectionTitle}>Warnings</Typography>
      <Box className={classes.warningRow}>
        {warnings.map((warning) => (
          <Box key={warning.title} className={classes.warningCard}>
            <Typography className={classes.warningTitle}>{warning.title}</Typography>
            <Typography className={classes.warningText}>{warning.text}</Typography>
          </Box>
        ))}
      </Box>

      <Typography className={classes.footer}>
        This panel is intentionally deterministic and based on current metadata, liquidity snapshots, and available pool stats. It frames tradeability and source quality, but does not infer holder concentration or rug risk without dedicated holder-distribution data.
      </Typography>
    </Box>
  );
}
